const ICONS = {
  suggested: "fa-regular fa-compass",
  district: "fa-solid fa-location-dot",
  destination: "fa-solid fa-tree",
  camp: "fa-solid fa-campground",
  safari: "fa-solid fa-paw",
  cruise: "fa-solid fa-sailboat",
  experience: "fa-solid fa-person-hiking",
};

const normalize = (value = "") =>
  value
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const buildTokens = (...parts) => normalize(parts.filter(Boolean).join(" "));
const toSlugToken = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const sortByRating = (a, b) => (b.rating || 0) - (a.rating || 0);

const pushToMap = (map, key, item) => {
  if (!key) return;
  const current = map.get(key) || [];
  current.push(item);
  map.set(key, current);
};

export const buildLocationSearchIndex = (destinations) => {
  const sections = {
    suggested: [],
    district: [],
    destination: [],
    camp: [],
    safari: [],
    cruise: [],
    experience: [],
  };

  const districtMap = new Map();
  const destinationMap = new Map();
  const destinationsByDistrict = new Map();
  const campsByDistrict = new Map();
  const campsByDestination = new Map();
  const safarisByDistrict = new Map();
  const safarisByDestination = new Map();
  const cruisesByDistrict = new Map();
  const cruisesByDestination = new Map();
  const experiencesByDistrict = new Map();
  const experiencesByDestination = new Map();

  Object.entries(destinations || {}).forEach(([slug, data]) => {
    const districtName = data.district?.trim();
    const title = data.title?.trim();

    if (districtName && !districtMap.has(districtName)) {
      const districtItem = {
        id: `district:${districtName}`,
        type: "district",
        label: districtName,
        caption: "All stays in this district",
        district: districtName,
        icon: ICONS.district,
        searchText: buildTokens(districtName, title, slug),
      };
      sections.district.push(districtItem);
      districtMap.set(districtName, districtItem);
    }

    const destinationItem = {
      id: `destination:${slug}`,
      type: "destination",
      label: title,
      caption: data.subtitle || `Explore ${title}`,
      district: districtName,
      destinationSlug: slug,
      icon: ICONS.destination,
      rating: data.rating,
      searchText: buildTokens(title, districtName, data.subtitle, slug),
    };
    sections.destination.push(destinationItem);
    destinationMap.set(slug, destinationItem);
    pushToMap(destinationsByDistrict, districtName, destinationItem);

    const locationMap = new Map();
    const accommodations = data.stay?.accommodations || [];
    accommodations.forEach((stay) => {
      const locationLabel = stay.location?.trim();
      if (!locationLabel) return;
      if (locationMap.has(locationLabel)) return;
      locationMap.set(locationLabel, true);
      const campId = `camp:${slug}:${locationLabel}`;
      sections.camp.push({
        id: campId,
        type: "camp",
        label: locationLabel,
        caption: `${title} · ${districtName}`,
        district: districtName,
        destinationSlug: slug,
        campSlug: toSlugToken(locationLabel),
        location: locationLabel,
        icon: ICONS.camp,
        searchText: buildTokens(locationLabel, title, districtName, slug),
      });
      pushToMap(campsByDestination, slug, sections.camp[sections.camp.length - 1]);
      pushToMap(campsByDistrict, districtName, sections.camp[sections.camp.length - 1]);
    });
  });

  const suggested = [...sections.destination]
    .sort(sortByRating)
    .slice(0, 5)
    .map((item) => ({
      ...item,
      type: "suggested",
      icon: ICONS.suggested,
      section: "suggested",
    }));

  sections.suggested = suggested;

  const allItems = [
    ...sections.suggested,
    ...sections.district,
    ...sections.destination,
    ...sections.camp,
    ...sections.safari,
    ...sections.cruise,
    ...sections.experience,
  ];

  return {
    sections,
    items: allItems,
    lookup: {
      destinationBySlug: destinationMap,
      districtByName: districtMap,
      destinationsByDistrict,
      campsByDistrict,
      campsByDestination,
      safarisByDistrict,
      safarisByDestination,
      cruisesByDistrict,
      cruisesByDestination,
      experiencesByDistrict,
      experiencesByDestination,
    },
  };
};

const normalizeTokens = (value = "") => {
  const trimmed = normalize(value);
  if (!trimmed) return [];
  return trimmed.split(" ").filter(Boolean);
};

const matchesTokens = (item, tokens) => {
  if (!tokens.length) return true;
  const haystack = String(item?.searchText || "");
  return tokens.every((token) => haystack.includes(token));
};

const limitList = (list = [], tokens, limit) => {
  const filtered = tokens?.length ? list.filter((item) => matchesTokens(item, tokens)) : list;
  return typeof limit === "number" ? filtered.slice(0, limit) : filtered;
};

const normalizeCategoryKey = (value) => String(value || "").trim().toLowerCase();

const buildExperienceSections = ({
  items = [],
  categories = [],
  selectedCategory,
  trimmed,
}) => {
  const categoryMap = new Map();
  categories.forEach((entry) => {
    const key = normalizeCategoryKey(entry.slug || entry.key || entry.id);
    if (!key) return;
    categoryMap.set(key, { ...entry, slug: key, label: entry.label || entry.name || key });
  });

  const matches = trimmed
    ? items.filter((item) => item.searchText.includes(trimmed))
    : items;
  const buckets = new Map();
  matches.forEach((item) => {
    const key = normalizeCategoryKey(item.categorySlug || item.category || "") || "other";
    const list = buckets.get(key) || [];
    list.push(item);
    buckets.set(key, list);
  });

  const sections = [];
  const selectedKey = normalizeCategoryKey(selectedCategory);
  if (selectedKey) {
    const selectedBucket = buckets.get(selectedKey) || [];
    if (selectedBucket.length) {
      const meta = categoryMap.get(selectedKey) || {
        slug: selectedKey,
        label: selectedKey.replace(/-/g, " "),
      };
      sections.push({
        key: `category:${selectedKey}`,
        title: meta.label,
        items: selectedBucket,
      });
      buckets.delete(selectedKey);
    }
  }

  categoryMap.forEach((meta, key) => {
    if (key === selectedKey) return;
    const bucket = buckets.get(key) || [];
    if (!bucket.length) return;
    sections.push({
      key: `category:${key}`,
      title: meta.label,
      items: bucket,
    });
    buckets.delete(key);
  });

  buckets.forEach((bucket, key) => {
    if (!bucket.length) return;
    sections.push({
      key: `category:${key}`,
      title: key === "other" ? "Other experiences" : "Other experiences",
      items: bucket,
    });
  });

  return sections;
};

export const filterLocationResults = (index, query, context = { type: "all" }, options = {}) => {
  const rawQuery = String(query || "");
  const rawTrimmed = rawQuery.trim();
  const tokens = normalizeTokens(rawQuery);
  const trimmed = tokens.join(" ");
  const { sections, items, lookup, meta } = index || {};
  if (!sections) {
    return [];
  }
  const mode = options.mode || "stays";
  const selectedCategory = options.experienceCategory || "";
  const categoryList = Array.isArray(meta?.categories) ? meta.categories : [];

  const buildEmpty = (message) => [
    {
      key: "empty",
      title: "No matches",
      items: [],
      message,
    },
  ];

  if (rawTrimmed && !trimmed) {
    return buildEmpty(`No locations match “${rawQuery}”. Try another name or district.`);
  }

  if (context.type === "district" && context.district) {
    const destinations = lookup.destinationsByDistrict?.get(context.district) || [];
    const camps = lookup.campsByDistrict?.get(context.district) || [];
    const safaris = lookup.safarisByDistrict?.get(context.district) || [];
    const cruises = lookup.cruisesByDistrict?.get(context.district) || [];
    const experiences = lookup.experiencesByDistrict?.get(context.district) || [];

    const filteredDestinations = limitList(destinations, tokens);
    const filteredCamps = limitList(camps, tokens);

    const results = [];
    if (mode === "experiences") {
      const experienceItems = [...safaris, ...cruises, ...experiences];
      results.push(
        ...buildExperienceSections({
          items: experienceItems,
          categories: categoryList,
          selectedCategory,
          trimmed,
        })
      );
    }

    if (filteredDestinations.length) {
      results.push({
        key: "destinations",
        title: `Destinations in ${context.district}`,
        items: filteredDestinations,
      });
    }
    if (filteredCamps.length) {
      results.push({
        key: "camps",
        title: `Nature camps in ${context.district}`,
        items: filteredCamps,
      });
    }
    if (mode !== "experiences") {
      if (safaris.length) {
        results.push({
          key: "safaris",
          title: `Experiences in ${context.district}`,
          items: limitList(safaris, tokens),
        });
      }
      if (cruises.length) {
        results.push({
          key: "cruises",
          title: `Cruise & boating in ${context.district}`,
          items: limitList(cruises, tokens),
        });
      }
      if (experiences.length) {
        results.push({
          key: "experiences",
          title: `Experiences in ${context.district}`,
          items: limitList(experiences, tokens),
        });
      }
    }

    if (!trimmed) {
      const otherDistricts = (sections.district || []).filter(
        (item) => item.district !== context.district
      );
      if (otherDistricts.length) {
        results.push({
          key: "other-districts",
          title: "Explore other districts",
          items: otherDistricts.slice(0, 5),
        });
      }
    }

    if (results.length) return results;
    if (trimmed) {
      return buildEmpty(`No locations in ${context.district} match “${query}”. Try another name.`);
    }
    return buildEmpty(`No destinations available for ${context.district} right now.`);
  }

  if (context.type === "destination" && context.destinationSlug) {
    const destination = lookup.destinationBySlug?.get(context.destinationSlug);
    const camps = lookup.campsByDestination?.get(context.destinationSlug) || [];
    const filteredCamps = limitList(camps, tokens);
    const safaris = lookup.safarisByDestination?.get(context.destinationSlug) || [];
    const cruises = lookup.cruisesByDestination?.get(context.destinationSlug) || [];
    const experiences = lookup.experiencesByDestination?.get(context.destinationSlug) || [];

    const results = [];
    if (mode === "experiences") {
      const experienceItems = [...safaris, ...cruises, ...experiences];
      results.push(
        ...buildExperienceSections({
          items: experienceItems,
          categories: categoryList,
          selectedCategory,
          trimmed,
        })
      );
    }
    if (destination && filteredCamps.length) {
      results.push({
        key: "camps",
        title: `Nature camps in ${destination.label}`,
        items: filteredCamps,
      });
    }
    if (mode !== "experiences") {
      if (safaris.length) {
        results.push({
          key: "safaris",
          title: `Experiences from ${destination?.label || "this destination"}`,
          items: limitList(safaris, tokens),
        });
      }
      if (cruises.length) {
        results.push({
          key: "cruises",
          title: `Cruises & boating`,
          items: limitList(cruises, tokens),
        });
      }
      if (experiences.length) {
        results.push({
          key: "experiences",
          title: `Experiences nearby`,
          items: limitList(experiences, tokens),
        });
      }
    }

    if (destination) {
      const siblingDestinations = (lookup.destinationsByDistrict?.get(destination.district) || []).filter(
        (item) => item.destinationSlug !== context.destinationSlug
      );
      const filteredSiblings = limitList(siblingDestinations, tokens);
      if (filteredSiblings.length) {
        results.push({
          key: "other-destinations",
          title: `Other destinations in ${destination.district}`,
          items: filteredSiblings,
        });
      }
      if (!trimmed) {
        const otherDistricts = (sections.district || []).filter(
          (item) => item.district !== destination.district
        );
        if (otherDistricts.length) {
          results.push({
            key: "other-districts",
            title: "Explore other districts",
          items: otherDistricts,
          });
        }
      }
    }

    if (results.length) return results;
    if (trimmed) {
      return buildEmpty(`No camps around this destination match “${query}”. Try another stay.`);
    }
    return buildEmpty("No camps available for this destination at the moment.");
  }

  const defaultSections = () => {
    const defaults = [
      {
        key: "suggested",
        title: "Suggested destinations",
        items: sections.suggested,
      },
      {
        key: "districts",
        title: "Districts",
        items: sections.district,
      },
      {
        key: "destinations",
        title: "Popular destinations",
        items: sections.destination,
      },
      {
        key: "camps",
        title: "Nature camps & stays",
        items: sections.camp,
      },
    ];
    if (mode === "experiences") {
      const experienceSections = buildExperienceSections({
        items: [...(sections.safari || []), ...(sections.cruise || []), ...(sections.experience || [])],
        categories: categoryList,
        selectedCategory,
        trimmed: "",
      });
      if (experienceSections.length) {
        defaults.unshift(...experienceSections);
      }
    }
    if (mode !== "experiences") {
      if (sections.safari?.length) {
        defaults.push({
          key: "safaris",
          title: "Experiences",
          items: sections.safari,
        });
      }
      if (sections.cruise?.length) {
        defaults.push({
          key: "cruises",
          title: "Cruises & boating",
          items: sections.cruise,
        });
      }
      if (sections.experience?.length) {
        defaults.push({
          key: "experiences",
          title: "Experiences",
          items: sections.experience,
        });
      }
    }
    return defaults.filter((section) => section.items?.length);
  };

  if (!trimmed) {
    return defaultSections();
  }

  const matches = items.filter((item) => matchesTokens(item, tokens));

  const districtMatches = matches.filter((item) => item.type === "district");
  const destinationMatches = matches.filter((item) =>
    ["destination", "suggested"].includes(item.type)
  );
  const campMatches = matches.filter((item) => item.type === "camp");
  const safariMatches = matches.filter((item) => item.type === "safari");
  const cruiseMatches = matches.filter((item) => item.type === "cruise");
  const experienceMatches = matches.filter(
    (item) => item.type === "experience" || item.type === "trek"
  );

  const results = [];
  if (mode === "experiences") {
    const experienceSections = buildExperienceSections({
      items: [...safariMatches, ...cruiseMatches, ...experienceMatches],
      categories: categoryList,
      selectedCategory,
      trimmed,
    });
    if (experienceSections.length) {
      results.push(...experienceSections);
    }
  }
  if (destinationMatches.length) {
    results.push({
      key: "destinations",
      title: "Destinations",
      items: destinationMatches,
    });
  }
  if (districtMatches.length) {
    results.push({
      key: "districts",
      title: "Districts",
      items: districtMatches,
    });
  }
  if (campMatches.length) {
    results.push({
      key: "camps",
      title: "Camps & stays",
      items: campMatches,
    });
  }
  if (mode !== "experiences") {
    if (safariMatches.length) {
      results.push({
        key: "safaris",
        title: "Safaris",
        items: safariMatches,
      });
    }
    if (cruiseMatches.length) {
      results.push({
        key: "cruises",
        title: "Cruises & boating",
        items: cruiseMatches,
      });
    }
    if (experienceMatches.length) {
      results.push({
        key: "experiences",
        title: "Experiences",
        items: experienceMatches,
      });
    }
  }

  if (!results.length) {
    return buildEmpty(`No locations match “${query}”. Try another name or district.`);
  }

  return results;
};

const toSimpleMap = (value) => {
  if (!value) return new Map();
  if (value instanceof Map) return value;
  return new Map(Object.entries(value));
};

const toArrayMap = (value) => {
  if (!value) return new Map();
  if (value instanceof Map) return value;
  const map = new Map();
  Object.entries(value).forEach(([key, entries]) => {
    map.set(key, Array.isArray(entries) ? entries : []);
  });
  return map;
};

export const deserializeLocationSearchIndex = (payload = {}) => {
  if (!payload || typeof payload !== "object") return null;
  const sections = payload.sections || {};
  return {
    sections: {
      suggested: sections.suggested || [],
      district: sections.district || [],
      destination: sections.destination || [],
      camp: sections.camp || [],
      safari: sections.safari || [],
      cruise: sections.cruise || [],
      experience: sections.experience || [],
    },
    items: Array.isArray(payload.items) ? payload.items : [],
    lookup: {
      destinationBySlug: toSimpleMap(payload.lookup?.destinationBySlug),
      districtByName: toSimpleMap(payload.lookup?.districtByName),
      destinationsByDistrict: toArrayMap(payload.lookup?.destinationsByDistrict),
      campsByDistrict: toArrayMap(payload.lookup?.campsByDistrict),
      campsByDestination: toArrayMap(payload.lookup?.campsByDestination),
      safarisByDistrict: toArrayMap(payload.lookup?.safarisByDistrict),
      safarisByDestination: toArrayMap(payload.lookup?.safarisByDestination),
      cruisesByDistrict: toArrayMap(payload.lookup?.cruisesByDistrict),
      cruisesByDestination: toArrayMap(payload.lookup?.cruisesByDestination),
      experiencesByDistrict: toArrayMap(payload.lookup?.experiencesByDistrict),
      experiencesByDestination: toArrayMap(payload.lookup?.experiencesByDestination),
    },
    meta: payload.meta || {},
  };
};
